/* eslint-disable */
// @ts-nocheck

/* eslint-enable prettier/prettier */
/* eslint-enable @typescript-eslint/no-unused-vars */
/* eslint-enable import/no-unresolved */

import React, { useEffect, useCallback, useState } from "react";
import PropTypes from "prop-types";

import { List } from "@material-ui/core";
import {
  selectBackendView,
  selectIsFocusViewTemplate,
} from "@untyped-store/slices/metaSlice";

import { useSelector, useDispatch } from "react-redux";
import { getSurveyResults } from "@untyped-store/slices/survey/surveyResultsSlice";
import { useAdminLocalStorageService } from "@admin";

import SurveyResultsQuestionRow from "./SurveyResultsQuestionRow";
import TextSizeControl from "./TextSizeControl";

import { makeStyles } from "@material-ui/core/styles";
import SurveyTitle from "../SurveyForm/SurveyTitle";
import SurveyDescription from "../SurveyForm/SurveyDescription";
import useOptimizedPolling from "@untyped-components/hooks/useOptimizedPolling";
import Spinner from "@untyped-components/Spinner/Spinner";

const useStyles = makeStyles(() => ({
  root: {
    fontSize: "13px",
    fontFamily: '"Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
    color: "#717171",
    width: "100%",
  },
}));

function SurveyResults({ survey }) {
  const dispatch = useDispatch();
  const surveyQuestions = useSelector((state) => state.surveyResults.questions);
  const isBackend = useSelector(selectBackendView);
  const isFocusViewTemplate = useSelector(selectIsFocusViewTemplate);
  const loading = useSelector((state) => state.surveyResults.loading);

  // Text size state and localStorage persistence
  // Only use admin localStorage when in backend view
  const adminLocalStorage = useAdminLocalStorageService();

  const [textSize, setTextSize] = useState(() => {
    if (isBackend) {
      const savedSize = adminLocalStorage.surveyResultsTextSize.read();
      return savedSize || 1.0; // Default to 1.0rem (10px baseline)
    }
    return 1.0; // Always 1.0rem in public view
  });

  const classes = useStyles();

  // Handle text size changes
  const handleTextSizeChange = useCallback(
    (newSize) => {
      console.log("[SurveyResults] Text size change:", newSize);
      setTextSize(newSize);
      if (isBackend && adminLocalStorage.surveyResultsTextSize) {
        adminLocalStorage.surveyResultsTextSize.write(newSize);
      }
    },
    [isBackend, adminLocalStorage],
  );

  const styling = {
    overflowY: "auto",
    maxWidth: "100%",
  };

  if (!isBackend && isFocusViewTemplate) {
    styling.maxHeight = "70vh";
  }

  // Create memoized poll function to avoid recreating on every render
  const pollSurveyResults = useCallback(async () => {
    if (survey && survey.id) {
      console.debug(
        "[SurveyResults] Polling survey results for survey ID:",
        survey.id,
      );
      const result = await dispatch(getSurveyResults({ encodedId: survey.id }));

      // Check if the Redux action was rejected (error occurred)
      if (getSurveyResults.rejected.match(result)) {
        const error =
          result.error.message || "Unknown error fetching survey results";
        console.error("[SurveyResults] Error fetching survey results:", error);
        throw new Error(error); // Throw to let useOptimizedPolling handle backoff
      } else {
        console.debug("[SurveyResults] Successfully fetched survey results");
      }
    } else {
      console.debug(
        "[SurveyResults] No survey or survey ID available for polling",
      );
    }
  }, [dispatch, survey?.id]);

  // Initial load on mount
  useEffect(() => {
    if (survey && survey.id) {
      console.debug("[SurveyResults] Initial load for survey ID:", survey.id);
      dispatch(getSurveyResults({ encodedId: survey.id }));
    } else {
      console.debug(
        "[SurveyResults] No survey or survey ID available for initial load",
      );
    }
  }, [survey?.id]);

  // Optimized polling: 10 seconds interval, exponential backoff on errors
  useOptimizedPolling({
    interval: 10,
    maxRetries: 10,
    baseDelay: 15,
    maxDelay: 300,
    onPoll: pollSurveyResults,
    enabled: !!(survey && survey.id), // Only poll when survey is available
  });

  // Debug: Log polling state changes
  useEffect(() => {
    const isPollingEnabled = !!(survey && survey.id);
    console.debug(
      "[SurveyResults] Polling enabled:",
      isPollingEnabled,
      "Survey ID:",
      survey?.id,
    );
  }, [survey?.id]);

  if (loading && !surveyQuestions?.length) {
    return (
      <div
        className={classes.root}
        style={{ textAlign: "center", marginTop: "20px" }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <section
      className={classes.root}
      aria-label={I18n.t("frontend.survey_results")}
    >
      {!isBackend && (
        <>
          <SurveyTitle text={survey.name} />
          <SurveyDescription text={survey.description} />
        </>
      )}
      <hr className="line line-lg line-dashed pull-in" aria-hidden />
      {isBackend && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingRight: "1.3rem",
          }}
        >
          <style>{`
            .survey-text-size-control label {
              margin: 0;
              margin-right: 0.8rem;
            }
            .survey-text-size-control .fui-FluentProvider {
              background-color: transparent;
            }
          `}</style>
          <div className="survey-text-size-control">
            <TextSizeControl
              textSize={textSize}
              onTextSizeChange={handleTextSizeChange}
              label={I18n.t("surveys.text_size_label")}
              min={1.0}
              max={1.75}
              step={0.25}
              minLabel="S"
              maxLabel="XL"
            />
          </div>
        </div>
      )}
      <List id="survey-results" style={styling} component={"div"}>
        {surveyQuestions?.map((question) =>
          survey?.id ? (
            <SurveyResultsQuestionRow
              surveyQuestion={question}
              key={question.id}
              textSize={isBackend ? textSize : 1.0}
            />
          ) : null,
        )}
      </List>
    </section>
  );
}

SurveyResults.propTypes = {
  survey: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
  }).isRequired,
};

export default SurveyResults;
